import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';

/**
 * CustomFieldDefinitionModal — the ONE create flow for a custom-field
 * definition, shared by the central Systems page (CustomFieldsAdmin) and the
 * in-profile Manage-fields region (CustomFieldsPanel). It POSTs through the real
 * key-generation + validation endpoint (POST /api/custom-fields/definitions) so
 * the field_key is derived from the label server-side; we show a live preview
 * and surface the server's 4xx messages (duplicate key → 409, bad options → 400)
 * verbatim.
 *
 * Scope is set by the caller: pass `clientId` for a client-scoped field or
 * `propertyId` for a property-scoped field (property wins if both are given,
 * matching the server's resolveDefinitionScope).
 *
 * Reuse-suggestion (PRD 2.6b): while the user types a label that matches a field
 * already defined elsewhere in the org, an advisory card offers to reuse it — so
 * the same field collapses to the same deterministic field_key across clients
 * instead of drifting into near-duplicates. It is purely advisory: a miss (or an
 * empty/stale catalog) falls back to the normal create flow untouched.
 *
 * Domain 19 / PRD 2.6 — Per-Client Custom Fields.
 */

export type CustomFieldDataType = 'text' | 'number' | 'date' | 'boolean' | 'select';

export const DATA_TYPE_OPTIONS: Array<{ value: CustomFieldDataType; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'select', label: 'Select (pick list)' },
];

export const dataTypeLabel: Record<CustomFieldDataType, string> = {
  text: 'Text', number: 'Number', date: 'Date', boolean: 'Yes / No', select: 'Select',
};

/** The full definition row as returned by the generic-CRUD list surface. */
export interface CustomFieldDefinition {
  id: string;
  client_id: string | null;
  property_id: string | null;
  field_key: string;
  label: string;
  data_type: CustomFieldDataType;
  options_json: string | null;
  display_order: number;
  is_active: boolean;
}

/**
 * Client-side mirror of the server's generateFieldKey (src/services/
 * custom-fields.js) — label → snake_case key, so the preview matches what the
 * backend will actually store. The server is authoritative; this is just UX.
 */
export function fieldKeyPreview(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64) || 'field';
}

export interface CatalogEntry {
  field_key: string;
  label: string;
  data_type: CustomFieldDataType;
  options: string[] | null;
  usedByCount: number;
}

function safeParseOptions(json: string | null): string[] | null {
  if (!json) return null;
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v.map((o) => String(o)) : null;
  } catch { return null; }
}

/** Dedupe every org definition by field_key into an advisory reuse catalog. */
export function buildCatalog(defs: CustomFieldDefinition[]): CatalogEntry[] {
  const byKey = new Map<string, { def: CustomFieldDefinition; scopes: Set<string> }>();
  for (const d of defs) {
    const scopeId = d.property_id || d.client_id;
    if (!scopeId) continue;
    const entry = byKey.get(d.field_key);
    if (entry) entry.scopes.add(scopeId);
    else byKey.set(d.field_key, { def: d, scopes: new Set([scopeId]) });
  }
  return [...byKey.values()].map(({ def, scopes }) => ({
    field_key: def.field_key,
    label: def.label,
    data_type: def.data_type,
    options: safeParseOptions(def.options_json),
    usedByCount: scopes.size,
  }));
}

export interface CustomFieldDefinitionModalProps {
  open: boolean;
  onClose: () => void;
  /** Client-scope target (omit when property-scoped). */
  clientId?: string;
  /** Property-scope target — wins over clientId when both are set. */
  propertyId?: string;
  /** Human scope label, e.g. "this client" / "this property". */
  scopeLabel: string;
  /** field_keys already defined in this scope — never suggest reusing one. */
  existingKeys?: string[];
  onCreated: () => void;
}

export function CustomFieldDefinitionModal({
  open, onClose, clientId, propertyId, scopeLabel, existingKeys = [], onCreated,
}: CustomFieldDefinitionModalProps) {
  const { toast } = useToast();
  const [label, setLabel] = useState('');
  const [dataType, setDataType] = useState<CustomFieldDataType>('text');
  const [optionsText, setOptionsText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reused, setReused] = useState(false);

  function reset() {
    setLabel(''); setDataType('text'); setOptionsText(''); setError(null); setReused(false);
  }

  // Reset whenever the modal (re)opens so it never carries stale state.
  useEffect(() => { if (open) reset(); }, [open]);

  // Advisory reuse catalog — every definition in the org, deduped by field_key.
  const catalogQuery = useQuery<CustomFieldDefinition[]>({
    queryKey: ['custom-field-catalog'],
    queryFn: async ({ signal }) => {
      const resp = await api.get<CustomFieldDefinition[] | { rows: CustomFieldDefinition[] }>(
        '/api/custom-field-definitions?limit=200', signal,
      );
      return Array.isArray(resp) ? resp : resp.rows;
    },
    enabled: open,
    staleTime: 2 * 60_000,
  });

  const catalog = useMemo(() => buildCatalog(catalogQuery.data ?? []), [catalogQuery.data]);

  const suggestion = useMemo<CatalogEntry | null>(() => {
    if (reused) return null;
    const lower = label.trim().toLowerCase();
    if (lower.length < 2) return null;
    const previewKey = fieldKeyPreview(label);
    if (existingKeys.includes(previewKey)) return null; // already in this scope
    return catalog.find((c) =>
      !existingKeys.includes(c.field_key)
      && (c.field_key === previewKey || c.label.toLowerCase().includes(lower)),
    ) ?? null;
  }, [catalog, label, existingKeys, reused]);

  function applySuggestion(entry: CatalogEntry) {
    setLabel(entry.label);
    setDataType(entry.data_type);
    setOptionsText((entry.options ?? []).join(', '));
    setReused(true);
    setError(null);
  }

  async function submit() {
    setError(null);
    if (!label.trim()) { setError('Give the field a label.'); return; }
    const body: Record<string, unknown> = { label: label.trim(), data_type: dataType };
    if (propertyId) body.property_id = propertyId; else body.client_id = clientId;
    if (dataType === 'select') {
      body.options = optionsText.split(',').map((o) => o.trim()).filter(Boolean);
    }
    setSubmitting(true);
    try {
      await api.post('/api/custom-fields/definitions', body);
      toast({
        variant: 'success',
        message: reused
          ? `Reused "${label.trim()}" on ${scopeLabel}.`
          : `Added "${label.trim()}" to ${scopeLabel}.`,
      });
      reset();
      onCreated();
    } catch (err) {
      // The server returns { error } with a human message (400/409); show it.
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+: /, '') : 'Could not create the field.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const primaryLabel = reused ? `Apply to ${scopeLabel}` : 'Add field';

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="New custom field"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>
            {submitting ? 'Adding…' : primaryLabel}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        {suggestion && (
          <div style={{
            padding: 'var(--sp-3) var(--sp-4)',
            background: 'var(--brand-primary-bg)',
            border: '1px solid var(--brand-primary)',
            borderRadius: 'var(--r-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}>
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>
              You already use <strong>"{suggestion.label}"</strong> ({dataTypeLabel[suggestion.data_type]})
              {' '}on {suggestion.usedByCount} other {suggestion.usedByCount === 1 ? 'client' : 'clients'} — use it again?
            </span>
            <div>
              <Button variant="primary" size="sm" onClick={() => applySuggestion(suggestion)}>
                Reuse this field
              </Button>
            </div>
          </div>
        )}
        {reused && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
            fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)',
          }}>
            <Badge variant="info" dot>Reusing existing field</Badge>
            <button
              type="button"
              onClick={() => setReused(false)}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                color: 'var(--brand-primary)', fontSize: 'var(--fs-sm)', textDecoration: 'underline',
              }}
            >Not this? Start over</button>
          </div>
        )}
        <Field label="Label">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Roof Replaced (Year)"
            disabled={reused}
            autoFocus
          />
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)', marginTop: 'var(--sp-1)' }}>
            Key: <span style={{ fontFamily: 'var(--font-mono)' }}>{fieldKeyPreview(label || 'field')}</span>
            {' '}· generated automatically
          </span>
        </Field>
        <Field label="Type">
          <Select
            options={DATA_TYPE_OPTIONS}
            value={dataType}
            disabled={reused}
            onChange={(e) => setDataType(e.target.value as CustomFieldDataType)}
          />
        </Field>
        {dataType === 'select' && (
          <Field label="Options (comma-separated)">
            <Input
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              placeholder="e.g. Low, Medium, High"
              disabled={reused}
            />
          </Field>
        )}
        {error && (
          <div style={{
            padding: 'var(--sp-3)', background: 'var(--color-status-red-bg)',
            border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
            borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)',
          }}>{error}</div>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{
        fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
      }}>{label}</span>
      {children}
    </label>
  );
}
